import { createTheme, ThemeOptions } from '@mui/material/styles';

type PaletteMode = 'light' | 'dark';
export type ThemeVariant = 'heritage' | 'blue' | 'green' | 'orange' | 'purple' | 'christmas';

interface ColorPalette {
  primary: {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  };
  secondary: {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  };
  background: {
    default: string;
    paper: string;
    variant: string;
  };
  text: {
    primary: string;
    secondary: string;
    disabled: string;
  };
  success: {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  };
  warning: {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  };
  error: {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  };
  info: {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  };
  divider: string;
  tree: {
    trunk: string;
    branch: string;
    leaf: string;
    accent: string;
  };
}

// ===== HERITAGE THEME (Brown/Tan) =====
const heritageLight: ColorPalette = {
  primary: {
    main: '#8B4513',
    light: '#A0522D',
    dark: '#5D2E0C',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#2F5233',
    light: '#4A7C59',
    dark: '#1B3520',
    contrastText: '#ffffff',
  },
  background: {
    default: '#F9F6F1',
    paper: '#FFFFFF',
    variant: '#F5F0E8',
  },
  text: {
    primary: '#2C2416',
    secondary: '#6B5D4F',
    disabled: '#B8ADA0',
  },
  success: {
    main: '#2F5233',
    light: '#4A7C59',
    dark: '#1B3520',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#C8802E',
    light: '#DAA520',
    dark: '#9B6B20',
    contrastText: '#ffffff',
  },
  error: {
    main: '#A84432',
    light: '#C95A47',
    dark: '#7D3125',
    contrastText: '#ffffff',
  },
  info: {
    main: '#6B7FA8',
    light: '#8B9FC5',
    dark: '#4E5F7F',
    contrastText: '#ffffff',
  },
  divider: '#E0D5C7',
  tree: {
    trunk: '#6B4423',
    branch: '#8B5A3C',
    leaf: '#5F7D5A',
    accent: '#C8802E',
  },
};

const heritageDark: ColorPalette = {
  primary: {
    main: '#C8935F',
    light: '#D4A574',
    dark: '#A0744A',
    contrastText: '#1A1512',
  },
  secondary: {
    main: '#7FA87E',
    light: '#9BBFA0',
    dark: '#5F7D5F',
    contrastText: '#1A1512',
  },
  background: {
    default: '#1A1512',
    paper: '#241F1A',
    variant: '#2D2621',
  },
  text: {
    primary: '#F5EDE3',
    secondary: '#C4B5A0',
    disabled: '#6B5D4F',
  },
  success: {
    main: '#7FA87E',
    light: '#9BBFA0',
    dark: '#5F7D5F',
    contrastText: '#1A1512',
  },
  warning: {
    main: '#D4A574',
    light: '#E3BE95',
    dark: '#B38A5C',
    contrastText: '#1A1512',
  },
  error: {
    main: '#D47B6A',
    light: '#E39B8E',
    dark: '#B35D4F',
    contrastText: '#1A1512',
  },
  info: {
    main: '#8B9FC5',
    light: '#A3B5D9',
    dark: '#6B7FA8',
    contrastText: '#1A1512',
  },
  divider: '#3D342C',
  tree: {
    trunk: '#9B7653',
    branch: '#B08D68',
    leaf: '#7FA87E',
    accent: '#D4A574',
  },
};

// ===== BLUE THEME =====
const blueLight: ColorPalette = {
  primary: {
    main: '#1565C0',
    light: '#1976D2',
    dark: '#0D47A1',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#0277BD',
    light: '#0288D1',
    dark: '#01579B',
    contrastText: '#ffffff',
  },
  background: {
    default: '#F0F7FF',
    paper: '#FFFFFF',
    variant: '#E3F2FD',
  },
  text: {
    primary: '#0D2847',
    secondary: '#4A5D75',
    disabled: '#A8B4C2',
  },
  success: {
    main: '#2E7D32',
    light: '#4CAF50',
    dark: '#1B5E20',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ED6C02',
    light: '#FF9800',
    dark: '#E65100',
    contrastText: '#ffffff',
  },
  error: {
    main: '#D32F2F',
    light: '#EF5350',
    dark: '#C62828',
    contrastText: '#ffffff',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
    contrastText: '#ffffff',
  },
  divider: '#BBDEFB',
  tree: {
    trunk: '#5D4E37',
    branch: '#795548',
    leaf: '#66BB6A',
    accent: '#1976D2',
  },
};

const blueDark: ColorPalette = {
  primary: {
    main: '#42A5F5',
    light: '#64B5F6',
    dark: '#1976D2',
    contrastText: '#000000',
  },
  secondary: {
    main: '#29B6F6',
    light: '#4FC3F7',
    dark: '#0288D1',
    contrastText: '#000000',
  },
  background: {
    default: '#0A1929',
    paper: '#132F4C',
    variant: '#1E3A5F',
  },
  text: {
    primary: '#E3F2FD',
    secondary: '#B3D4FC',
    disabled: '#5B7A99',
  },
  success: {
    main: '#66BB6A',
    light: '#81C784',
    dark: '#4CAF50',
    contrastText: '#000000',
  },
  warning: {
    main: '#FFA726',
    light: '#FFB74D',
    dark: '#FF9800',
    contrastText: '#000000',
  },
  error: {
    main: '#EF5350',
    light: '#E57373',
    dark: '#D32F2F',
    contrastText: '#000000',
  },
  info: {
    main: '#29B6F6',
    light: '#4FC3F7',
    dark: '#0288D1',
    contrastText: '#000000',
  },
  divider: '#1E3A5F',
  tree: {
    trunk: '#8D6E63',
    branch: '#A1887F',
    leaf: '#81C784',
    accent: '#64B5F6',
  },
};

// ===== GREEN THEME =====
const greenLight: ColorPalette = {
  primary: {
    main: '#2E7D32',
    light: '#388E3C',
    dark: '#1B5E20',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#558B2F',
    light: '#689F38',
    dark: '#33691E',
    contrastText: '#ffffff',
  },
  background: {
    default: '#F1F8F4',
    paper: '#FFFFFF',
    variant: '#E8F5E9',
  },
  text: {
    primary: '#1B3A1F',
    secondary: '#4A5C4D',
    disabled: '#A5B5A8',
  },
  success: {
    main: '#2E7D32',
    light: '#4CAF50',
    dark: '#1B5E20',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#F57C00',
    light: '#FF9800',
    dark: '#E65100',
    contrastText: '#ffffff',
  },
  error: {
    main: '#C62828',
    light: '#D32F2F',
    dark: '#B71C1C',
    contrastText: '#ffffff',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
    contrastText: '#ffffff',
  },
  divider: '#C8E6C9',
  tree: {
    trunk: '#5D4E37',
    branch: '#6D5D3F',
    leaf: '#66BB6A',
    accent: '#81C784',
  },
};

const greenDark: ColorPalette = {
  primary: {
    main: '#66BB6A',
    light: '#81C784',
    dark: '#4CAF50',
    contrastText: '#000000',
  },
  secondary: {
    main: '#9CCC65',
    light: '#AED581',
    dark: '#8BC34A',
    contrastText: '#000000',
  },
  background: {
    default: '#0D1F12',
    paper: '#1A2E1F',
    variant: '#233D2A',
  },
  text: {
    primary: '#E8F5E9',
    secondary: '#B9E4BD',
    disabled: '#5A6E5D',
  },
  success: {
    main: '#81C784',
    light: '#A5D6A7',
    dark: '#66BB6A',
    contrastText: '#000000',
  },
  warning: {
    main: '#FFB74D',
    light: '#FFCC80',
    dark: '#FFA726',
    contrastText: '#000000',
  },
  error: {
    main: '#E57373',
    light: '#EF5350',
    dark: '#D32F2F',
    contrastText: '#000000',
  },
  info: {
    main: '#4FC3F7',
    light: '#81D4FA',
    dark: '#29B6F6',
    contrastText: '#000000',
  },
  divider: '#2C4F34',
  tree: {
    trunk: '#8D6E63',
    branch: '#A1887F',
    leaf: '#81C784',
    accent: '#AED581',
  },
};

// ===== ORANGE THEME =====
const orangeLight: ColorPalette = {
  primary: {
    main: '#E65100',
    light: '#FF6F00',
    dark: '#BF360C',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#F57C00',
    light: '#FF9800',
    dark: '#EF6C00',
    contrastText: '#ffffff',
  },
  background: {
    default: '#FFF8F0',
    paper: '#FFFFFF',
    variant: '#FFE8D6',
  },
  text: {
    primary: '#3E2723',
    secondary: '#5D4037',
    disabled: '#A1887F',
  },
  success: {
    main: '#388E3C',
    light: '#4CAF50',
    dark: '#2E7D32',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#F57C00',
    light: '#FF9800',
    dark: '#EF6C00',
    contrastText: '#ffffff',
  },
  error: {
    main: '#C62828',
    light: '#D32F2F',
    dark: '#B71C1C',
    contrastText: '#ffffff',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
    contrastText: '#ffffff',
  },
  divider: '#FFCCBC',
  tree: {
    trunk: '#5D4E37',
    branch: '#6D5D3F',
    leaf: '#66BB6A',
    accent: '#FF9800',
  },
};

const orangeDark: ColorPalette = {
  primary: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#000000',
  },
  secondary: {
    main: '#FFAB40',
    light: '#FFC947',
    dark: '#FF9100',
    contrastText: '#000000',
  },
  background: {
    default: '#1A0F08',
    paper: '#2D1810',
    variant: '#3D2318',
  },
  text: {
    primary: '#FFE8D6',
    secondary: '#FFCCBC',
    disabled: '#6D5447',
  },
  success: {
    main: '#66BB6A',
    light: '#81C784',
    dark: '#4CAF50',
    contrastText: '#000000',
  },
  warning: {
    main: '#FFB74D',
    light: '#FFCC80',
    dark: '#FFA726',
    contrastText: '#000000',
  },
  error: {
    main: '#EF5350',
    light: '#E57373',
    dark: '#D32F2F',
    contrastText: '#000000',
  },
  info: {
    main: '#29B6F6',
    light: '#4FC3F7',
    dark: '#0288D1',
    contrastText: '#000000',
  },
  divider: '#4D3428',
  tree: {
    trunk: '#8D6E63',
    branch: '#A1887F',
    leaf: '#81C784',
    accent: '#FFAB40',
  },
};

// ===== PURPLE THEME =====
const purpleLight: ColorPalette = {
  primary: {
    main: '#6A1B9A',
    light: '#7B1FA2',
    dark: '#4A148C',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#8E24AA',
    light: '#9C27B0',
    dark: '#6A1B9A',
    contrastText: '#ffffff',
  },
  background: {
    default: '#F9F5FC',
    paper: '#FFFFFF',
    variant: '#F3E5F5',
  },
  text: {
    primary: '#311B92',
    secondary: '#512DA8',
    disabled: '#B39DDB',
  },
  success: {
    main: '#388E3C',
    light: '#4CAF50',
    dark: '#2E7D32',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#F57C00',
    light: '#FF9800',
    dark: '#EF6C00',
    contrastText: '#ffffff',
  },
  error: {
    main: '#C62828',
    light: '#D32F2F',
    dark: '#B71C1C',
    contrastText: '#ffffff',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
    contrastText: '#ffffff',
  },
  divider: '#E1BEE7',
  tree: {
    trunk: '#5D4E37',
    branch: '#6D5D3F',
    leaf: '#66BB6A',
    accent: '#AB47BC',
  },
};

const purpleDark: ColorPalette = {
  primary: {
    main: '#BA68C8',
    light: '#CE93D8',
    dark: '#AB47BC',
    contrastText: '#000000',
  },
  secondary: {
    main: '#E040FB',
    light: '#EA80FC',
    dark: '#D500F9',
    contrastText: '#000000',
  },
  background: {
    default: '#1A0A1F',
    paper: '#2C1535',
    variant: '#3D2047',
  },
  text: {
    primary: '#F3E5F5',
    secondary: '#E1BEE7',
    disabled: '#6A4F7A',
  },
  success: {
    main: '#66BB6A',
    light: '#81C784',
    dark: '#4CAF50',
    contrastText: '#000000',
  },
  warning: {
    main: '#FFB74D',
    light: '#FFCC80',
    dark: '#FFA726',
    contrastText: '#000000',
  },
  error: {
    main: '#EF5350',
    light: '#E57373',
    dark: '#D32F2F',
    contrastText: '#000000',
  },
  info: {
    main: '#29B6F6',
    light: '#4FC3F7',
    dark: '#0288D1',
    contrastText: '#000000',
  },
  divider: '#4A2F5A',
  tree: {
    trunk: '#8D6E63',
    branch: '#A1887F',
    leaf: '#81C784',
    accent: '#CE93D8',
  },
};

// ===== CHRISTMAS THEME =====
const christmasLight: ColorPalette = {
  primary: {
    main: '#C62828',
    light: '#D32F2F',
    dark: '#B71C1C',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#2E7D32',
    light: '#388E3C',
    dark: '#1B5E20',
    contrastText: '#ffffff',
  },
  background: {
    default: '#FFF9F5',
    paper: '#FFFFFF',
    variant: '#FFF0E6',
  },
  text: {
    primary: '#1B1B1B',
    secondary: '#4A4A4A',
    disabled: '#9E9E9E',
  },
  success: {
    main: '#2E7D32',
    light: '#4CAF50',
    dark: '#1B5E20',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#F57C00',
    light: '#FF9800',
    dark: '#EF6C00',
    contrastText: '#ffffff',
  },
  error: {
    main: '#C62828',
    light: '#D32F2F',
    dark: '#B71C1C',
    contrastText: '#ffffff',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
    contrastText: '#ffffff',
  },
  divider: '#FFD7CC',
  tree: {
    trunk: '#5D4E37',
    branch: '#6D5D3F',
    leaf: '#66BB6A',
    accent: '#FFD700',
  },
};

const christmasDark: ColorPalette = {
  primary: {
    main: '#EF5350',
    light: '#E57373',
    dark: '#D32F2F',
    contrastText: '#000000',
  },
  secondary: {
    main: '#66BB6A',
    light: '#81C784',
    dark: '#4CAF50',
    contrastText: '#000000',
  },
  background: {
    default: '#0D1A0F',
    paper: '#1C2E1E',
    variant: '#2A3D2C',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#E8F5E9',
    disabled: '#5D6E5F',
  },
  success: {
    main: '#81C784',
    light: '#A5D6A7',
    dark: '#66BB6A',
    contrastText: '#000000',
  },
  warning: {
    main: '#FFB74D',
    light: '#FFCC80',
    dark: '#FFA726',
    contrastText: '#000000',
  },
  error: {
    main: '#EF5350',
    light: '#E57373',
    dark: '#D32F2F',
    contrastText: '#000000',
  },
  info: {
    main: '#29B6F6',
    light: '#4FC3F7',
    dark: '#0288D1',
    contrastText: '#000000',
  },
  divider: '#3D4F3F',
  tree: {
    trunk: '#8D6E63',
    branch: '#A1887F',
    leaf: '#81C784',
    accent: '#FFD700',
  },
};

// Theme variant configurations
const themeVariants = {
  heritage: { light: heritageLight, dark: heritageDark },
  blue: { light: blueLight, dark: blueDark },
  green: { light: greenLight, dark: greenDark },
  orange: { light: orangeLight, dark: orangeDark },
  purple: { light: purpleLight, dark: purpleDark },
  christmas: { light: christmasLight, dark: christmasDark },
};

// Design tokens for consistent styling
export const tokens = {
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },
  borderRadius: {
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
    full: '50%',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  transitions: {
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out',
  },
};

// Heritage typography - Serif for headings, sans-serif for body
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontFamily: '"Playfair Display", "Georgia", "Times New Roman", serif',
    fontWeight: 700,
    fontSize: '2.75rem',
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h2: {
    fontFamily: '"Playfair Display", "Georgia", "Times New Roman", serif',
    fontWeight: 600,
    fontSize: '2.25rem',
    lineHeight: 1.25,
    letterSpacing: '-0.008em',
  },
  h3: {
    fontFamily: '"Playfair Display", "Georgia", "Times New Roman", serif',
    fontWeight: 600,
    fontSize: '1.875rem',
    lineHeight: 1.3,
    letterSpacing: '0em',
  },
  h4: {
    fontFamily: '"Merriweather", "Georgia", serif',
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: 1.35,
    letterSpacing: '0.005em',
  },
  h5: {
    fontFamily: '"Merriweather", "Georgia", serif',
    fontWeight: 500,
    fontSize: '1.25rem',
    lineHeight: 1.4,
    letterSpacing: '0em',
  },
  h6: {
    fontFamily: '"Merriweather", "Georgia", serif',
    fontWeight: 500,
    fontSize: '1.125rem',
    lineHeight: 1.45,
    letterSpacing: '0.0075em',
  },
  body1: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: '1rem',
    lineHeight: 1.6,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    letterSpacing: '0.01071em',
  },
  button: {
    fontWeight: 500,
    fontSize: '0.875rem',
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'none' as const,
  },
};

// Enhanced component customizations
const getComponents = (mode: PaletteMode, colors: ColorPalette) => {
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          textTransform: 'none' as const,
          fontWeight: 500,
          transition: tokens.transitions.normal,
          '&:focus-visible': {
            outline: `2px solid ${colors.primary.main}`,
            outlineOffset: 2,
          },
        },
        contained: {
          boxShadow: tokens.shadows.sm,
          '&:hover': {
            boxShadow: tokens.shadows.md,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.lg,
          boxShadow: tokens.shadows.sm,
          border: `1px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
          transition: tokens.transitions.normal,
          '&:hover': {
            boxShadow: tokens.shadows.md,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: tokens.borderRadius.md,
            transition: tokens.transitions.normal,
            backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: tokens.shadows.sm,
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: tokens.shadows.sm,
        },
        elevation2: {
          boxShadow: tokens.shadows.md,
        },
        elevation3: {
          boxShadow: tokens.shadows.lg,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.sm,
          fontWeight: 500,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.875rem',
          backgroundColor: colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          transition: tokens.transitions.fast,
          '&:focus-visible': {
            outline: `2px solid ${colors.primary.main}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          marginBottom: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: `${tokens.borderRadius.md}px ${tokens.borderRadius.md}px 0 0`,
          '&.Mui-selected': {
            fontWeight: 600,
          },
          variants: [],
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          border: '1px solid',
        },
      },
    },
  };
};

// Create theme based on mode and variant
export const createGenealogyTheme = (mode: PaletteMode, variant: ThemeVariant = 'heritage') => {
  const colors = themeVariants[variant][mode];

  return createTheme({
    palette: {
      mode,
      primary: colors.primary,
      secondary: colors.secondary,
      background: colors.background,
      text: colors.text,
      success: colors.success,
      warning: colors.warning,
      error: colors.error,
      info: colors.info,
      divider: colors.divider,
      tree: colors.tree,
    },
    typography,
    components: getComponents(mode, colors),
    spacing: 8,
    shape: {
      borderRadius: tokens.borderRadius.md,
    },
  });
};

// Default theme (heritage light)
export const theme = createGenealogyTheme('light', 'heritage');

// Export color palettes for use in components
export const heritageColors = { light: heritageLight, dark: heritageDark };
export const blueColors = { light: blueLight, dark: blueDark };
export const greenColors = { light: greenLight, dark: greenDark };
export const orangeColors = { light: orangeLight, dark: orangeDark };
export const purpleColors = { light: purpleLight, dark: purpleDark };
export const christmasColors = { light: christmasLight, dark: christmasDark };

// For backward compatibility
export const lightColors = heritageLight;
export const darkColors = heritageDark;

// Theme type augmentation for custom colors
declare module '@mui/material/styles' {
  interface Palette {
    tree: {
      trunk: string;
      branch: string;
      leaf: string;
      accent: string;
    };
  }

  interface PaletteOptions {
    tree?: {
      trunk?: string;
      branch?: string;
      leaf?: string;
      accent?: string;
    };
  }
}

export default theme;
